"use client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IconArrowRight } from "@/lib/icons";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import Container from "../container";
import CMSLink from "../common/CMSLink";
import SliderNavButtons from "../common/SliderNavButtons";
import CardRowSkeleton from "@/components/CMS/CardRowSkeleton";
import styles from "./blogSection.module.css";
import { isCityAllowed } from "@/lib/cityVisibility";
import {
  buildDynamicContext,
  resolveDynamicVariables,
} from "@/lib/replaceDynamicValues";
import { getBlogs } from "./getBlogs";

// Import the shared BlogCard to keep the design consistent
import SharedBlogCard from "./BlogCard";
import { bindDotWindow } from "@/lib/swiperDots";
import { Flex } from "antd";

const DEFAULT_PAGE_SIZE = 6;
// Start fetching the next page when the user is this many slides from the end,
// so the new cards are usually in place before they reach the last one.
const PREFETCH_THRESHOLD = 2;

const resolvePosts = (posts, dynamicContext, city) =>
  (resolveDynamicVariables(posts, dynamicContext) || [])
    .filter((post) => post && typeof post === "object")
    .filter((post) => isCityAllowed(post?.for_which_city, city));

// `initialPosts` / `initialTotal` are page 1 fetched on the server
// (ServerBlogSection), so the slider's post links are in the HTML crawlers get.
export default function BlogSection({
  data,
  city,
  initialPosts,
  initialTotal,
}) {
  const [mounted, setMounted] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [paginationEl, setPaginationEl] = useState(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  // Fixed dot count — set once after first load, never grows with load-more
  const [dotCount, setDotCount] = useState(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (swiperInstance && paginationEl && !swiperInstance.destroyed) {
      swiperInstance.params.pagination.el = paginationEl;
      if (swiperInstance.pagination) {
        swiperInstance.pagination.destroy();
        swiperInstance.pagination.init();
        swiperInstance.pagination.render();
        swiperInstance.pagination.update();
      }
      return bindDotWindow(swiperInstance, paginationEl);
    }
  }, [swiperInstance, paginationEl]);

  // The section's heading/description/"See all" button still come from the
  // CMS, but the posts themselves come from the paginated blogs API (the same
  // one the blog listing page uses) so the slider always shows current blogs.
  const pageSize = Number(data?.limit) || DEFAULT_PAGE_SIZE;
  const dynamicContext = useMemo(() => buildDynamicContext({ city }), [city]);
  const hasInitial = Array.isArray(initialPosts);

  const [posts, setPosts] = useState(() =>
    hasInitial ? resolvePosts(initialPosts, dynamicContext, city) : [],
  );
  const [total, setTotal] = useState(hasInitial ? initialTotal || 0 : 0);
  const [page, setPage] = useState(() =>
    !hasInitial ? 0 : initialPosts.length > 0 ? 1 : Number.POSITIVE_INFINITY,
  );
  const [initialLoading, setInitialLoading] = useState(!hasInitial);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingRef = useRef(false);
  const requestIdRef = useRef(0);
  const skipFirstLoadRef = useRef(hasInitial);
  // Initialise dotCount from SSR posts so dots are ready on first paint
  useEffect(() => {
    if (hasInitial && initialPosts.length > 0 && dotCount === 0) {
      setDotCount(initialPosts.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPage = useCallback(
    async (pageNum) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      const requestId = requestIdRef.current;
      if (pageNum > 1) setLoadingMore(true);

      const { data: fetched, total: fetchedTotal } = await getBlogs({
        limit: pageSize,
        page: pageNum,
        endpoint: data?.endpoint,
        city,
      });

      // A newer city/page-size reset happened while this was in flight.
      if (requestId !== requestIdRef.current) return;

      const resolved = resolvePosts(fetched, dynamicContext, city);

      setPosts((prev) => {
        if (pageNum === 1) return resolved;
        const seen = new Set(prev.map((p) => p.id ?? p.slug));
        return [...prev, ...resolved.filter((p) => !seen.has(p.id ?? p.slug))];
      });
      // Freeze dot count after first page — don't let load-more grow the dots
      if (pageNum === 1) setDotCount(resolved.length);
      setTotal(fetchedTotal);
      // An empty page means the API has nothing more, whatever `total` says.
      setPage(fetched.length > 0 ? pageNum : Number.POSITIVE_INFINITY);
      setInitialLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    },
    [pageSize, data?.endpoint, city, dynamicContext],
  );

  useEffect(() => {
    // Page 1 for this city came from the server; reset only on a later change.
    if (skipFirstLoadRef.current) {
      skipFirstLoadRef.current = false;
      return;
    }
    requestIdRef.current += 1;
    loadingRef.current = false;
    setPosts([]);
    setTotal(0);
    setPage(0);
    setInitialLoading(true);
    setLoadingMore(false);
    loadPage(1);
  }, [loadPage]);

  const hasMore = Number.isFinite(page) && page > 0 && page * pageSize < total;

  const maybeLoadMore = useCallback(
    (swiper) => {
      if (!hasMore || loadingRef.current || !swiper) return;
      const perView =
        typeof swiper.params?.slidesPerView === "number"
          ? swiper.params.slidesPerView
          : 1;
      const lastVisible = swiper.activeIndex + Math.ceil(perView);
      if (swiper.isEnd || lastVisible >= posts.length - PREFETCH_THRESHOLD) {
        loadPage(page + 1);
      }
    },
    [hasMore, posts.length, page, loadPage],
  );
  // Swiper binds its event handlers once, so read the latest callback via ref.
  const maybeLoadMoreRef = useRef(maybeLoadMore);
  maybeLoadMoreRef.current = maybeLoadMore;
  const handleSlideProgress = useCallback(
    (swiper) => maybeLoadMoreRef.current(swiper),
    [],
  );

  if (initialLoading) {
    return (
      <section className="flex flex-col gap-[20px] my-6 md:my-8">
        <Container>
          <div className="flex flex-col gap-5">
            {data?.title && <h2 className={styles.title}>{data.title}</h2>}
            <CardRowSkeleton
              count={3}
              fixedHeight={185}
              radius={16}
              gap={24}
              cardClassName={styles.skeletonCard}
            />
          </div>
        </Container>
      </section>
    );
  }

  if (!posts || posts.length === 0) return null;

  const { title, description, button } = data;
  const viewAll = button?.[0];
  const seeAllText = viewAll?.label || "See all";

  return (
    <section className="flex flex-col gap-[20px] my-6 md:my-8">
      <Container>
        <Flex vertical gap={20}>
          <div className={styles.headerRow}>
            <div className={styles.headerLeft}>
              {title && <h2 className={styles.title}>{title}</h2>}
              {viewAll?.url ? (
                <CMSLink
                  config={viewAll}
                  useIn="header"
                  city={city}
                  className={`${styles.seeAllLink} !hidden md:!inline-flex items-center pt-[10px]`}
                >
                  {seeAllText} <IconArrowRight size={14} />
                </CMSLink>
              ) : (
                <span
                  className={`${styles.seeAllLink} !hidden md:!inline-flex items-center pt-[10px]`}
                >
                  {seeAllText} <IconArrowRight size={14} />
                </span>
              )}
            </div>

            <div className={styles.headerRight}>
              {viewAll?.url ? (
                <CMSLink
                  config={viewAll}
                  useIn="header"
                  city={city}
                  className={`${styles.seeAllLink} !inline-flex md:!hidden items-center`}
                >
                  {seeAllText} <IconArrowRight size={14} />
                </CMSLink>
              ) : (
                <span
                  className={`${styles.seeAllLink} !inline-flex md:!hidden items-center`}
                >
                  {seeAllText} <IconArrowRight size={14} />
                </span>
              )}

              {posts.length > 3 && (
                <SliderNavButtons
                  prevRef={prevRef}
                  nextRef={nextRef}
                  swiper={swiperInstance}
                  itemsCount={posts.length}
                />
              )}
            </div>
          </div>

          {description && <p className={styles.description}>{description}</p>}

          {initialLoading && posts.length === 0 ? (
            <CardRowSkeleton
              count={3}
              fixedHeight={185}
              radius={16}
              gap={16}
              cardClassName={styles.skeletonCard}
            />
          ) : !mounted ? (
            <CardRowSkeleton
              count={3}
              fixedHeight={185}
              radius={16}
              gap={16}
              cardClassName={styles.skeletonCard}
            />
          ) : (
            <>
              <Swiper
                modules={[Navigation, Pagination]}
                onSwiper={setSwiperInstance}
                onInit={(swiper) => {
                  swiper.params.navigation.prevEl = prevRef.current;
                  swiper.params.navigation.nextEl = nextRef.current;
                  swiper.navigation.init();
                  swiper.navigation.update();
                }}
                onSlideChange={handleSlideProgress}
                onReachEnd={handleSlideProgress}
                navigation={{
                  prevEl: prevRef.current,
                  nextEl: nextRef.current,
                }}
                pagination={
                  dotCount > 1
                    ? {
                      el: paginationEl,
                      clickable: true,
                    }
                    : false
                }
                slidesPerView={1.5}
                spaceBetween={16}
                breakpoints={{
                  480: {
                    slidesPerView: 2.1,
                    spaceBetween: 16,
                  },
                  768: {
                    slidesPerView: 3,
                    spaceBetween: 20,
                  },
                  992: {
                    slidesPerView: 3,
                    spaceBetween: 24,
                  },
                }}
                className={`w-full ${styles.blogSwiper}`}
              >
                {posts.map((post, index) => (
                  <SwiperSlide
                    key={post.id ?? post.slug ?? post.url?.url ?? index}
                    className="!h-auto flex"
                  >
                    <SharedBlogCard item={post} />
                  </SwiperSlide>
                ))}
                {loadingMore && (
                  <SwiperSlide className="!h-auto flex">
                    <div className="w-full min-h-[180px] rounded-xl bg-gray-200 animate-pulse" />
                  </SwiperSlide>
                )}
              </Swiper>
              {dotCount > 1 && (
                <div
                  ref={setPaginationEl}
                  className="swiper-pagination mobile-slider-pagination"
                />
              )}
            </>
          )}
        </Flex>
      </Container>
    </section>
  );
}
